import React from 'react';
import styled from 'styled-components';

import logger from '../logging/logger';
import ErrorMessage from './ErrorMessage';

const Link = styled.a`
  text-decoration: underline;
  cursor: pointer;
`;

type ErrorInfo = {
  componentStack: string;
};
type ErrorRenderingArgs = {
  refreshPage: React.ReactNode;
  reloadComponent: React.ReactNode;
  reloadCount: number;
};

type Props = {
  children: React.ReactNode;
  errorRendering?: (renderArgs: ErrorRenderingArgs) => React.ReactNode;
};

interface State {
  hasError: boolean;
  reloadCount: number;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state = {
    hasError: false,
    reloadCount: 0,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error(error.message, {
      componentStack: info.componentStack,
    });
  }

  handleRefreshPage = () => {
    window.location.reload();
  };

  handleReload = () => {
    this.setState({ hasError: false, reloadCount: this.state.reloadCount + 1 });
  };

  render() {
    const { children, errorRendering } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      const refreshPage = <Link onClick={this.handleRefreshPage}>refresh</Link>;
      const reloadComponent = <Link onClick={this.handleReload}>reload</Link>;

      const defaultErrorMessage = (
        <ErrorMessage
          mainMessage="Uh oh! Looks like something went wrong."
          subMessage={
            <span>
              Try to {refreshPage} the page or request a new link from your
              Zillow representative.
            </span>
          }
        />
      );

      if (errorRendering) {
        // Attempt to render provided `errorRendering` but on exception, output fixed
        // error message to avoid possible infinite loop.
        return errorRendering({
          refreshPage,
          reloadComponent,
          reloadCount: this.state.reloadCount,
        });
      }

      return defaultErrorMessage;
    }

    return children || null;
  }
}
